import { forwardRef, useState, type ButtonHTMLAttributes } from 'react';

/**
 * AskAiButton — the gradient "Ask CompassAI" launcher that opens an AI chat panel.
 *
 * A headline brand affordance (accent→purple gradient pill with a sparkle
 * glyph and a soft lift on hover) that several surfaces render identically to
 * toggle their side chat panel — the dashboard viewer and the pipeline
 * overview. It was hand-rolled, byte-for-byte, in each place (same gradient,
 * same hover transform, same "Ask CompassAI" / "Hide CompassAI" label flip);
 * this owns that one look so the copies can't drift.
 *
 * While `open` the button flips to a neutral `--bg-tertiary` "Hide CompassAI"
 * toggle (the affordance stays put rather than disappearing). The visible label
 * is fixed ("Ask CompassAI" / "Hide CompassAI"); the accessible name is
 * caller-supplied because it describes what the panel is about (`closedLabel`).
 */

function SparkleGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4L12 3z" />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" />
    </svg>
  );
}

export interface AskAiButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Whether the AI chat panel this launches is currently open. */
  open: boolean;
  /** Accessible name while the panel is closed (e.g. "Ask about the pipeline"). */
  closedLabel: string;
  /** Accessible name while the panel is open. Default "Hide AI chat panel". */
  openLabel?: string;
}

const REST_SHADOW =
  '0 6px 16px -6px var(--accent-glow), 0 2px 4px -2px rgba(15,23,42,0.12)';
const HOVER_SHADOW =
  '0 10px 22px -6px var(--accent-glow), 0 4px 8px -2px rgba(15,23,42,0.16)';

export const AskAiButton = forwardRef<HTMLButtonElement, AskAiButtonProps>(
  function AskAiButton({ open, closedLabel, openLabel = 'Hide AI chat panel', type = 'button', style, ...rest }, ref) {
    const [hover, setHover] = useState(false);
    const lifted = !open && hover;
    return (
      <button
        ref={ref}
        type={type}
        aria-label={open ? openLabel : closedLabel}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
          padding: '8px 16px',
          borderRadius: 999,
          border: 'none',
          background: open
            ? 'var(--bg-tertiary)'
            : 'linear-gradient(135deg, var(--accent) 0%, var(--purple) 100%)',
          color: open ? 'var(--text-primary)' : 'white',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          boxShadow: open ? 'none' : lifted ? HOVER_SHADOW : REST_SHADOW,
          transform: lifted ? 'translateY(-1px)' : 'translateY(0)',
          transition: 'all 0.18s ease',
          ...style,
        }}
        {...rest}
      >
        <SparkleGlyph />
        {open ? 'Hide CompassAI' : 'Ask CompassAI'}
      </button>
    );
  },
);
